import React, { useState, useEffect, useCallback } from 'react';
import { dashboardService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import './DashboardPage.css';

const DashboardPage = () => {
  const { user, profile } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showWidgets, setShowWidgets] = useState(false);

  const balance = parseFloat(profile?.balance || 0);
  const currency = profile?.currency || 'USD';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const dashRes = await dashboardService.getClientDashboard();
      setData(dashRes.data || {});
      setError('');
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Defer TradingView widgets until after main content is painted
  useEffect(() => {
    if (!loading) {
      const t = setTimeout(() => setShowWidgets(true), 300);
      return () => clearTimeout(t);
    }
  }, [loading]);

  const getCurrencySymbol = (code) => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: 0 }).format(0).replace(/[\d\s,]/g, '').trim();
    } catch (err) {
      return '$';
    }
  };

  const formatAmt = (amt, curr) => {
    const sym = getCurrencySymbol(curr || currency);
    return `${sym}${parseFloat(amt || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric' }) : '—';

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase() || 'pending';
    const map = { pending: 'badge-warning', approved: 'badge-success', rejected: 'badge-danger' };
    return <span className={`badge ${map[s] || 'badge-secondary'}`}>{s.charAt(0).toUpperCase() + s.slice(1)}</span>;
  };

  const sym = getCurrencySymbol(currency);
  const balanceFmt = balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const gics = data?.gics || [];
  const bondsPortfolio = data?.bonds_portfolio || [];
  const treasuryBondsPortfolio = data?.treasury_bonds_portfolio || [];
  const equitiesPortfolio = data?.equities_portfolio || [];

  const portfolioBalance = (
    bondsPortfolio.reduce((s, p) => s + parseFloat(p.amount || 0), 0) +
    equitiesPortfolio.reduce((s, p) => s + parseFloat(p.amount || 0), 0) +
    gics.reduce((s, g) => s + parseFloat(g.amount || 0), 0)
  );
  const portfolioBalanceFmt = portfolioBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) {
    return (
      <div className="page dashboard-page">
        <div className="page-title-box">
          <h4 className="page-title-text">Dashboard</h4>
        </div>
        <div className="loading-state">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="page dashboard-page">
      <div className="page-title-box">
        <h4 className="page-title-text">Dashboard</h4>
        <div className="page-title-right">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/dashboard">Client</a></li>
            <li className="breadcrumb-item active">Dashboard</li>
          </ol>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Ticker tape — deferred */}
      {showWidgets && (
        <div className="tradingview-widget-container" style={{ width: '100%', height: '46px', marginBottom: '20px' }}>
          <iframe
            title="Market Ticker"
            scrolling="no"
            allowTransparency="true"
            frameBorder="0"
            loading="lazy"
            src="https://s.tradingview.com/embed-widget/ticker-tape/?%7B%22isTransparent%22%3Afalse%2C%22displayMode%22%3A%22adaptive%22%2C%22showSymbolLogo%22%3Afalse%2C%22width%22%3A%22100%25%22%2C%22height%22%3A46%7D"
            style={{ boxSizing: 'border-box', height: '46px', width: '100%' }}
          />
        </div>
      )}

      <div className="dashboard-layout">
        {/* Left column */}
        <div className="dashboard-left-column">

          {/* Stats cards */}
          <div className="stats-grid">
            <div className="stat-card stat-card--blue">
              <div className="stat-card-header">
                <div className="stat-icon"><i className="bx bx-wallet-alt"></i></div>
                <p className="stat-label">Portfolio Balance</p>
              </div>
              <h4 className="stat-value">{sym}{portfolioBalanceFmt}</h4>
            </div>

            <div className="stat-card stat-card--teal">
              <div className="stat-card-header">
                <div className="stat-icon"><i className="bx bx-wallet"></i></div>
                <p className="stat-label">Cash Balance</p>
              </div>
              <h4 className="stat-value">{sym}{balanceFmt}</h4>
            </div>

            <div className="stat-card stat-card--purple">
              <div className="stat-card-header">
                <div className="stat-icon"><i className="bx bx-briefcase"></i></div>
                <p className="stat-label">Bonds</p>
              </div>
              <h4 className="stat-value">
                {data?.bonds_portfolio_count || 0}
                <span className="stat-unit">positions</span>
              </h4>
            </div>

            <div className="stat-card stat-card--amber">
              <div className="stat-card-header">
                <div className="stat-icon"><i className="bx bx-bank"></i></div>
                <p className="stat-label">Treasury Bonds</p>
              </div>
              <h4 className="stat-value">
                {data?.treasury_bonds_portfolio_count || 0}
                <span className="stat-unit">positions</span>
              </h4>
            </div>

            <div className="stat-card stat-card--positive">
              <div className="stat-card-header">
                <div className="stat-icon"><i className="bx bx-trending-up"></i></div>
                <p className="stat-label">Equities</p>
              </div>
              <h4 className="stat-value">
                {data?.equities_portfolio_count || 0}
                <span className="stat-unit">positions</span>
              </h4>
            </div>
          </div>

          {/* Certificate of Deposits */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <h5 className="card-title">Certificate of Deposits</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table">
                  <thead className="table-light">
                    <tr>
                      <th style={{ textAlign: 'center' }}>Amount</th>
                      <th style={{ textAlign: 'center' }}>Percentage</th>
                      <th style={{ textAlign: 'center' }}>Payment Frequency</th>
                      <th style={{ textAlign: 'center' }}>Maturity Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!gics.length ? (
                      <tr><td colSpan="4" className="empty-state">No Certificate of Deposits found</td></tr>
                    ) : gics.map((g) => (
                      <tr key={g.id}>
                        <td style={{ textAlign: 'center' }}>{formatAmt(g.amount)}</td>
                        <td style={{ textAlign: 'center' }}>{g.percentage}%</td>
                        <td style={{ textAlign: 'center' }}>{g.redeemable || '—'}</td>
                        <td style={{ textAlign: 'center' }}>{formatDate(g.maturity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Bonds */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <h5 className="card-title">Bonds</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table">
                  <thead className="table-light">
                    <tr>
                      <th>Investment</th>
                      <th>Transaction Date</th>
                      <th>Amount</th>
                      <th>Maturity</th>
                      <th>Payout</th>
                      <th>Coupon Rate</th>
                      <th>ISIN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!bondsPortfolio.length ? (
                      <tr><td colSpan="7" className="empty-state">No bonds found</td></tr>
                    ) : bondsPortfolio.map((p) => (
                      <tr key={p.id}>
                        <td>{p.referenceable?.name || '—'}</td>
                        <td>{formatDate(p.created_at)}</td>
                        <td>{formatAmt(p.amount)}</td>
                        <td>{formatDate(p.referenceable?.maturity_term)}</td>
                        <td>{p.referenceable?.coupon_frquency || '—'}</td>
                        <td>{p.referenceable?.apr != null ? `${parseFloat(p.referenceable.apr).toFixed(5)}%` : '—'}</td>
                        <td>{p.referenceable?.isin || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Treasury Bonds */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <h5 className="card-title">Treasury Bonds</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table">
                  <thead className="table-light">
                    <tr>
                      <th>Investment</th>
                      <th>Transaction Date</th>
                      <th>Amount</th>
                      <th>Maturity</th>
                      <th>Payout</th>
                      <th>Coupon Rate</th>
                      <th>ISIN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!treasuryBondsPortfolio.length ? (
                      <tr><td colSpan="7" className="empty-state">No treasury bonds found</td></tr>
                    ) : treasuryBondsPortfolio.map((p) => (
                      <tr key={p.id}>
                        <td>{p.referenceable?.name || '—'}</td>
                        <td>{formatDate(p.created_at)}</td>
                        <td>{formatAmt(p.amount)}</td>
                        <td>{formatDate(p.referenceable?.maturity_term)}</td>
                        <td>{p.referenceable?.coupon_frquency || '—'}</td>
                        <td>{p.referenceable?.apr != null ? `${parseFloat(p.referenceable.apr).toFixed(5)}%` : '—'}</td>
                        <td>{p.referenceable?.isin || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Private Equity */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <h5 className="card-title">Private Equity</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table">
                  <thead className="table-light">
                    <tr>
                      <th>Company</th>
                      <th>Symbol</th>
                      <th>Capital Invested</th>
                      <th>No. of Shares</th>
                      <th>Share Price</th>
                      <th>Profit/Loss</th>
                      <th>Locked Period (Days)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!equitiesPortfolio.length ? (
                      <tr><td colSpan="7" className="empty-state">No equities found</td></tr>
                    ) : equitiesPortfolio.map((p) => {
                      const sharePrice = parseFloat(p.referenceable?.share_price || 0);
                      const shares = parseFloat(p.share || 0);
                      const invested = parseFloat(p.amount || 0);
                      const profitLoss = (sharePrice * shares) - invested;
                      return (
                        <tr key={p.id}>
                          <td>{p.referenceable?.name || '—'}</td>
                          <td>{p.referenceable?.symbol || '—'}</td>
                          <td>{formatAmt(invested)}</td>
                          <td>{shares.toLocaleString()}</td>
                          <td>{formatAmt(sharePrice)}</td>
                          <td style={{ color: profitLoss >= 0 ? '#28a745' : '#dc3545' }}>
                            {formatAmt(profitLoss)}
                          </td>
                          <td style={{ textAlign: 'center' }}>{p.lockin_period ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Latest Deposits */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <h5 className="card-title">Latest Deposits</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table">
                  <thead className="table-light">
                    <tr>
                      <th style={{ textAlign: 'center' }}>Amount</th>
                      <th style={{ textAlign: 'center' }}>Currency</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th style={{ textAlign: 'center' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!data?.recent_deposits?.length ? (
                      <tr><td colSpan="4" className="empty-state">No deposits found</td></tr>
                    ) : data.recent_deposits.map((d) => (
                      <tr key={d.id}>
                        <td style={{ textAlign: 'center' }}>{formatAmt(d.amount, d.currency)}</td>
                        <td style={{ textAlign: 'center' }}>{d.currency || '—'}</td>
                        <td style={{ textAlign: 'center' }}>{getStatusBadge(d.status)}</td>
                        <td style={{ textAlign: 'center' }}>{formatDate(d.date || d.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Latest Withdrawals */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Latest Withdrawals</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table">
                  <thead className="table-light">
                    <tr>
                      <th style={{ textAlign: 'center' }}>Amount</th>
                      <th style={{ textAlign: 'center' }}>Currency</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th style={{ textAlign: 'center' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!data?.recent_withdrawals?.length ? (
                      <tr><td colSpan="4" className="empty-state">No withdrawals found</td></tr>
                    ) : data.recent_withdrawals.map((w) => (
                      <tr key={w.id}>
                        <td style={{ textAlign: 'center' }}>{formatAmt(w.amount, w.currency)}</td>
                        <td style={{ textAlign: 'center' }}>{w.currency || '—'}</td>
                        <td style={{ textAlign: 'center' }}>{getStatusBadge(w.status)}</td>
                        <td style={{ textAlign: 'center' }}>{formatDate(w.date || w.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Right column — market widget deferred */}
        <div className="dashboard-right-column">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">World Markets</h5>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {showWidgets ? (
                <iframe
                  title="Market Overview"
                  scrolling="no"
                  allowTransparency="true"
                  frameBorder="0"
                  loading="lazy"
                  src="https://s.tradingview.com/embed-widget/market-overview/?%7B%22tabs%22%3A%5B%7B%22title%22%3A%22Indices%22%2C%22symbols%22%3A%5B%7B%22s%22%3A%22FOREXCOM%3ASPXUSD%22%2C%22d%22%3A%22S%26P%20500%22%7D%2C%7B%22s%22%3A%22FOREXCOM%3ANSXUSD%22%2C%22d%22%3A%22Nasdaq%22%7D%2C%7B%22s%22%3A%22FOREXCOM%3ADJI%22%2C%22d%22%3A%22Dow%22%7D%2C%7B%22s%22%3A%22INDEX%3ANIKKEI%22%2C%22d%22%3A%22Nikkei%22%7D%5D%7D%5D%2C%22colorTheme%22%3A%22light%22%2C%22isTransparent%22%3Afalse%2C%22width%22%3A%22100%25%22%2C%22height%22%3A500%7D"
                  style={{ boxSizing: 'border-box', width: '100%', height: '500px' }}
                />
              ) : (
                <div style={{ height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}>
                  Loading market data...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
